import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  computed,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { Appointment, LabJob } from '@principle-theorem/principle-core';
import {
  AppointmentStatus,
  AppointmentSummary,
  DEFAULT_STATUS_INDICATOR_CONFIG,
  getStatusIndicatorTypeFromAppointmentStatus,
  isAppointment,
  IStatusIndicatorConfig,
  LabJobBadgeState,
  MentionResourceType,
  StatusIndicatorType,
  StatusIndicatorMode,
  type IAppointment,
} from '@principle-theorem/principle-core/interfaces';
import {
  filterUndefined,
  shareReplayCold,
  toMoment,
  toTimestamp,
  WithRef,
} from '@principle-theorem/shared';
import { isString } from 'lodash';
import { combineLatest, type Observable } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import { CurrentScopeFacade } from '../../store/current-scope.facade';

@Component({
  selector: 'pr-timeline-appointment-notifications',
  templateUrl: './timeline-appointment-notifications.component.html',
  styleUrls: ['./timeline-appointment-notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgCommonModule, MatTooltipModule, MatIconModule],
})
export class TimelineAppointmentNotificationsComponent {
  private _currentScope = inject(CurrentScopeFacade);
  private _statusIndicatorOverrides$: Observable<
    | { [key in StatusIndicatorType]?: Partial<IStatusIndicatorConfig> }
    | undefined
  >;

  appointment = input.required<WithRef<IAppointment> | AppointmentSummary>();
  tooltipsEnabled = input(false);
  badgeSize = input(14);

  badgeStyles = computed(() => ({
    width: `${this.badgeSize()}px`,
    height: `${this.badgeSize()}px`,
  }));

  iconStyles = computed(() => ({
    width: `${this.badgeSize()}px`,
    height: `${this.badgeSize()}px`,
    'font-size': `${this.badgeSize()}px`,
  }));

  white = '#FFFFFF';
  statusIndicatorMode = StatusIndicatorMode;
  labJobBadgeState = LabJobBadgeState;

  showConfirmationBadge$: Observable<boolean>;
  showLabJobBadge$: Observable<boolean>;
  isOnlineBooking$: Observable<boolean>;
  isBookedExternally$: Observable<boolean>;
  isNotConfirmed$: Observable<boolean>;
  hasArrived$: Observable<boolean>;
  labJobBadgeState$: Observable<LabJobBadgeState>;

  appointmentStatusConfig$: Observable<IStatusIndicatorConfig | undefined>;
  notConfirmedConfig$: Observable<IStatusIndicatorConfig>;
  hasArrivedConfig$: Observable<IStatusIndicatorConfig>;
  labJobOverdueConfig$: Observable<IStatusIndicatorConfig>;
  labJobSentOrSendingConfig$: Observable<IStatusIndicatorConfig>;
  labJobReceivedConfig$: Observable<IStatusIndicatorConfig>;
  onlineBookingConfig$: Observable<IStatusIndicatorConfig>;
  bookedExternallyConfig$: Observable<IStatusIndicatorConfig>;

  externalBookingTooltip$: Observable<string | undefined>;
  appointmentStatusTooltip$: Observable<string>;

  constructor() {
    const appointment$ = toObservable(this.appointment);

    this._statusIndicatorOverrides$ = this._currentScope.currentPractice$.pipe(
      filterUndefined(),
      map((practice) => practice.settings.statusIndicatorOverrides),
      shareReplayCold()
    );

    const appointmentStatusIndicatorType$ = appointment$.pipe(
      map((appointment) => {
        const status = isAppointment(appointment)
          ? appointment.status
          : appointment.metadata.status;

        return status === AppointmentStatus.Unscheduled
          ? undefined
          : getStatusIndicatorTypeFromAppointmentStatus(status);
      }),
      shareReplayCold()
    );

    this.appointmentStatusConfig$ = combineLatest([
      appointmentStatusIndicatorType$,
      this._statusIndicatorOverrides$,
    ]).pipe(
      map(([indicatorType, overrides]) => {
        if (!indicatorType) {
          return;
        }

        return {
          ...DEFAULT_STATUS_INDICATOR_CONFIG[indicatorType],
          ...overrides?.[indicatorType],
        };
      })
    );

    this.appointmentStatusTooltip$ = appointmentStatusIndicatorType$.pipe(
      map((indicatorType) => indicatorType ?? '')
    );

    this.onlineBookingConfig$ = this.getStatusConfig$(
      StatusIndicatorType.OnlineBooking
    );

    this.bookedExternallyConfig$ = this.getStatusConfig$(
      StatusIndicatorType.BookedExternally
    );

    this.notConfirmedConfig$ = this.getStatusConfig$(
      StatusIndicatorType.AppointmentNotConfirmed
    );

    this.hasArrivedConfig$ = this.getStatusConfig$(
      StatusIndicatorType.AppointmentHasArrived
    );

    this.labJobOverdueConfig$ = this.getStatusConfig$(
      StatusIndicatorType.LabJobOverdue
    );

    this.labJobSentOrSendingConfig$ = this.getStatusConfig$(
      StatusIndicatorType.LabJobSentOrSending
    );

    this.labJobReceivedConfig$ = this.getStatusConfig$(
      StatusIndicatorType.LabJobReceived
    );

    this.isNotConfirmed$ = appointment$.pipe(
      map((appointment) => Appointment.isUnconfirmed(appointment))
    );

    this.hasArrived$ = appointment$.pipe(
      map((appointment) => Appointment.canRevertToConfirmed(appointment)),
      startWith(false)
    );

    this.isOnlineBooking$ = appointment$.pipe(
      map((appointment) => Appointment.isBookedOnline(appointment))
    );

    this.isBookedExternally$ = appointment$.pipe(
      map((appointment) => Appointment.isBookedExternally(appointment))
    );

    this.externalBookingTooltip$ = combineLatest([
      this.isBookedExternally$,
      appointment$,
    ]).pipe(
      map(([isBookedExternally, appointment]) => {
        if (!isBookedExternally) {
          return;
        }

        const createdBy = isAppointment(appointment)
          ? appointment.createdBy
          : appointment.metadata.createdBy;

        if (!createdBy || isString(createdBy)) {
          return;
        }

        if (createdBy.type === MentionResourceType.ApiKey) {
          return `${createdBy.name} (API Key) booked this appointment`;
        }

        if (createdBy.type === MentionResourceType.PartnerApp) {
          return `${createdBy.name} booked this appointment`;
        }
      })
    );

    const labJobs$ = appointment$.pipe(
      switchMap((appointment) => Appointment.labJobs$(appointment))
    );

    this.showLabJobBadge$ = labJobs$.pipe(map((labJobs) => !!labJobs.length));

    this.labJobBadgeState$ = combineLatest([labJobs$, appointment$]).pipe(
      map(([labJobs, appointment]) => {
        const isAfterAppointmentStart = appointment.event
          ? toMoment(appointment.event.from).isBefore(toMoment(toTimestamp()))
          : false;

        if (
          labJobs.some((labJob) => !!labJob.dueDate) ||
          isAfterAppointmentStart
        ) {
          const isOverdue = labJobs.some((labJob) => LabJob.isOverdue(labJob));
          if (isOverdue) {
            return LabJobBadgeState.Overdue;
          }
        }

        const isReceived = labJobs.some((labJob) => LabJob.complete(labJob));
        if (isReceived) {
          return LabJobBadgeState.Received;
        }

        return LabJobBadgeState.SentOrSending;
      })
    );
  }

  private getStatusConfig$(
    type: StatusIndicatorType
  ): Observable<IStatusIndicatorConfig> {
    return this._statusIndicatorOverrides$.pipe(
      map((overrides) => ({
        ...DEFAULT_STATUS_INDICATOR_CONFIG[type],
        ...overrides?.[type],
      }))
    );
  }
}
